/**
 * One physics step of player intent. Axes are in [-1, 1] except throttle and the brake levers in [0, 1].
 *
 * `brake` is the single lever of beginner mode (and of phase-1 callers): the sim splits it
 * `BikeConfig.combinedBrakeFront` to the front and the rest to the rear. `frontBrake` / `rearBrake` are the two
 * levers of the full scheme; when both a split lever and the single lever are pressed the stronger one wins per wheel.
 */
export interface InputFrame {
  throttle: number;
  brake: number;
  frontBrake?: number;
  rearBrake?: number;
  steer: number;
  /** Body axis: -1 back, 0 centre, +1 forward, continuous. */
  body: number;
  standing: boolean;
  /** Inner foot out (the side follows the lean). */
  foot?: boolean;
  /** Beginner assist: the foot goes out by itself in slow turns. */
  autoFoot?: boolean;
  /**
   * Simplified control mode (src/sim/assist.ts): the world turns the intent into assisted levers and the physics
   * adds its own help (balance, wheelie cap, air levelling, forgiving crashes). Recorded in the autoFoot slot of a
   * replay as 2, so the replay layout is unchanged.
   */
  assist?: boolean;
  /**
   * Automatic posture (MX physics row 1): the rider sits and stands by himself, and `standing` becomes "Space held",
   * which flips his choice while held. Without it `standing` is the stance itself (tests, bots, old replays).
   * Recorded as +4 in the autoFoot slot of a replay.
   */
  autoPosture?: boolean;
  /**
   * Set by the assist only (src/sim/assist.ts TRICK): the lean trick it is riding. The physics lets an asked wheelie
   * climb to its balance point (ASSIST.trickBand instead of the 15 deg cap). Not recorded: a replay re-runs the assist.
   */
  trick?: number;
}

export const NEUTRAL_INPUT: InputFrame = { throttle: 0, brake: 0, steer: 0, body: 0, standing: false };

/** Numbers per step in a v2 replay / input log. */
export const INPUT_STRIDE_V2 = 9;

/** Packs a frame into INPUT_STRIDE_V2 numbers (booleans as 0/1). */
export function encodeInput(i: InputFrame, out: number[]): void {
  out.push(i.throttle, i.brake, i.frontBrake ?? 0, i.rearBrake ?? 0, i.steer, i.body, i.standing ? 1 : 0, i.foot ? 1 : 0, (i.assist ? 2 : i.autoFoot ? 1 : 0) + (i.autoPosture ? 4 : 0));
}

/** Reads the frame at offset k; stride 5 is the phase-1 layout (throttle, brake, steer, body, standing). */
export function decodeInput(x: readonly number[], k: number, stride: number = INPUT_STRIDE_V2): InputFrame {
  if (stride === 5) return { throttle: x[k]!, brake: x[k + 1]!, steer: x[k + 2]!, body: x[k + 3]!, standing: x[k + 4] === 1 };
  return {
    throttle: x[k]!, brake: x[k + 1]!, frontBrake: x[k + 2]!, rearBrake: x[k + 3]!, steer: x[k + 4]!, body: x[k + 5]!,
    standing: x[k + 6] === 1, foot: x[k + 7] === 1, autoFoot: x[k + 8]! % 4 >= 1, assist: x[k + 8]! % 4 === 2,
    ...(x[k + 8]! >= 4 ? { autoPosture: true } : {}),
  };
}
